import { useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import { 
  TrendUp,
  Users,
  FileText,
  CheckSquare,
  Warning,
  CurrencyDollar,
  ArrowDown,
  ArrowUp
} from '@phosphor-icons/react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function Dashboard() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    try {
      const [dashRes, finRes] = await Promise.all([
        axios.get(`${API}/dashboard`, { withCredentials: true }),
        axios.get(`${API}/dashboard/financial`, { withCredentials: true })
      ]);
      setData({ ...dashRes.data, ...finRes.data });
    } catch (error) {
      console.error('Failed to load dashboard', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="p-8">
        <div className="animate-pulse space-y-4">
          <div className="h-10 bg-white/40 rounded-lg w-64"></div>
          <div className="grid grid-cols-4 gap-6">
            {[1,2,3,4].map(i => <div key={i} className="h-32 bg-white/40 rounded-2xl"></div>)}
          </div>
        </div>
      </div>
    );
  }

  const stats = [
    { name: 'Net Revenue', value: `R ${(data?.net_profit || data?.net_revenue || 0).toLocaleString()}`, icon: TrendUp, gradient: 'from-blue-600 to-cyan-500' },
    { name: 'Active Clients', value: data?.total_clients || 0, icon: Users, gradient: 'from-cyan-500 to-teal-500' },
    { name: 'Total Invoices', value: data?.total_invoices || 0, icon: FileText, gradient: 'from-indigo-600 to-blue-600' },
    { name: 'Pending Tasks', value: data?.pending_tasks || 0, icon: CheckSquare, gradient: 'from-blue-500 to-indigo-600' },
  ];

  return (
    <div className="p-8" data-testid="dashboard-page">
      <div className="mb-8">
        <h1 className="text-5xl font-bold tracking-tight ocean-text" style={{fontFamily: 'Cabinet Grotesk, sans-serif'}} data-testid="dashboard-title">
          Welcome back, {user?.name?.split(' ')[0]}
        </h1>
        <p className="mt-2 text-slate-600">Here's how your business is sailing today.</p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5 mb-8">
        {stats.map((stat) => {
          const Icon = stat.icon;
          return (
            <div key={stat.name} className="glass-card p-6" data-testid={`stat-${stat.name.toLowerCase().replace(/\s+/g, '-')}`}>
              <div className="flex items-start justify-between mb-3">
                <p className="text-xs font-bold uppercase tracking-widest text-slate-500">{stat.name}</p>
                <div className={`w-10 h-10 rounded-lg bg-gradient-to-br ${stat.gradient} flex items-center justify-center text-white shadow-md`}>
                  <Icon size={20} weight="fill" />
                </div>
              </div>
              <p className="text-4xl font-bold tracking-tight text-slate-900" style={{fontFamily: 'Cabinet Grotesk, sans-serif'}}>
                {stat.value}
              </p>
            </div>
          );
        })}
      </div>

      {/* Financial Overview */}
      <div className="glass-card p-6 mb-6" data-testid="financial-overview">
        <div className="flex items-center gap-3 mb-5">
          <div className="w-10 h-10 rounded-lg ocean-gradient flex items-center justify-center text-white shadow-md">
            <CurrencyDollar size={20} weight="fill" />
          </div>
          <h2 className="text-2xl font-bold" style={{fontFamily: 'Cabinet Grotesk, sans-serif'}}>Financial Overview</h2>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-5">
          <div className="bg-white/50 backdrop-blur-sm rounded-xl p-4 border border-slate-200/60">
            <div className="flex items-center gap-1 text-emerald-600 mb-1">
              <ArrowUp size={14} weight="bold" />
              <p className="text-[10px] font-bold uppercase tracking-widest">Revenue</p>
            </div>
            <p className="text-xl font-bold text-slate-900" data-testid="total-income">R {(data?.revenue || 0).toLocaleString()}</p>
          </div>
          <div className="bg-white/50 backdrop-blur-sm rounded-xl p-4 border border-slate-200/60">
            <div className="flex items-center gap-1 text-rose-500 mb-1">
              <ArrowDown size={14} weight="bold" />
              <p className="text-[10px] font-bold uppercase tracking-widest">Expenses</p>
            </div>
            <p className="text-xl font-bold text-slate-900" data-testid="total-expenses">R {(data?.expenses || 0).toLocaleString()}</p>
          </div>
          <div className="bg-white/50 backdrop-blur-sm rounded-xl p-4 border border-slate-200/60">
            <p className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-1">Net Profit</p>
            <p className="text-xl font-bold ocean-text" data-testid="net-revenue">R {(data?.net_profit || 0).toLocaleString()}</p>
          </div>
          <div className="bg-white/50 backdrop-blur-sm rounded-xl p-4 border border-slate-200/60">
            <p className="text-[10px] font-bold uppercase tracking-widest text-blue-600 mb-1">Debtors</p>
            <p className="text-xl font-bold text-slate-900" data-testid="outstanding-debtors">R {(data?.outstanding_debtors || 0).toLocaleString()}</p>
          </div>
          <div className="bg-white/50 backdrop-blur-sm rounded-xl p-4 border border-slate-200/60">
            <p className="text-[10px] font-bold uppercase tracking-widest text-amber-600 mb-1">Creditors</p>
            <p className="text-xl font-bold text-slate-900" data-testid="outstanding-creditors">R {(data?.outstanding_creditors || 0).toLocaleString()}</p>
          </div>
        </div>
      </div>

      {/* Alerts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="glass-card p-6" data-testid="expiring-documents-section">
          <h2 className="text-xl font-bold mb-4" style={{fontFamily: 'Cabinet Grotesk, sans-serif'}}>Expiring Documents</h2>
          {data?.expiring_documents?.length > 0 ? (
            <div className="space-y-2">
              {data.expiring_documents.map((doc) => (
                <div key={doc.id} className="flex items-start gap-3 p-3 rounded-lg bg-amber-50/80 border border-amber-200/60 backdrop-blur-sm" data-testid={`expiring-doc-${doc.id}`}>
                  <Warning size={18} className="text-amber-600 mt-0.5" weight="fill" />
                  <div className="flex-1">
                    <p className="text-sm font-semibold text-slate-900">{doc.name}</p>
                    <p className="text-xs text-slate-600 mt-0.5">Expires: {new Date(doc.expiry_date).toLocaleDateString()}</p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-slate-500 py-4 text-center" data-testid="no-expiring-docs">All documents valid</p>
          )}
        </div>

        <div className="glass-card p-6" data-testid="upcoming-compliance-section">
          <h2 className="text-xl font-bold mb-4" style={{fontFamily: 'Cabinet Grotesk, sans-serif'}}>Upcoming Compliance</h2>
          {data?.upcoming_compliance?.length > 0 ? (
            <div className="space-y-2">
              {data.upcoming_compliance.map((item) => (
                <div key={item.id} className="flex items-start gap-3 p-3 rounded-lg bg-rose-50/80 border border-rose-200/60 backdrop-blur-sm" data-testid={`compliance-${item.id}`}>
                  <Warning size={18} className="text-rose-600 mt-0.5" weight="fill" />
                  <div className="flex-1">
                    <p className="text-sm font-semibold text-slate-900">{item.type}</p>
                    <p className="text-xs text-slate-600 mt-0.5">Due: {new Date(item.due_date).toLocaleDateString()}</p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-slate-500 py-4 text-center" data-testid="no-upcoming-compliance">No upcoming deadlines</p>
          )}
        </div>
      </div>
    </div>
  );
}
